/**
 * 权限管理 Store (Zustand)
 * 基于三段式权限模型: module:resource:action
 * 
 * 支持两种模式:
 * 1. 本地模式 (Demo): 使用角色默认权限
 * 2. API 模式: 从后端获取权限列表
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserRole, RoleDefaultPermissions } from '../types/permission';
import { authService } from '../services';

// 是否使用 API 模式（可通过环境变量控制）
const USE_API_MODE = import.meta.env.VITE_USE_API_AUTH === 'true';

/**
 * 权限检查函数
 * @param {string[]} userPermissions - 用户权限列表
 * @param {string} permissionId - 权限ID (格式: module:resource:action)
 * @returns {boolean}
 */
const checkPermission = (userPermissions, permissionId) => {
  // 如果是管理员（拥有所有权限）
  if (userPermissions === '*' || userPermissions.includes('*')) {
    return true;
  }

  // 直接匹配
  if (userPermissions.includes(permissionId)) {
    return true;
  }

  // 检查管理权限继承 (manage 包含所有操作)
  const parts = permissionId.split(':');
  if (parts.length === 3) {
    const [module, resource] = parts;
    const managePermissionId = `${module}:${resource}:manage`;
    if (userPermissions.includes(managePermissionId)) {
      return true;
    }
  }

  return false;
};

/**
 * 获取角色默认权限
 * @param {string} role - 角色名
 * @returns {string[] | string}
 */
const getRolePermissions = (role) => {
 return RoleDefaultPermissions[role] || [];
};

/**
 * 权限 Store
 */
const usePermissionStore = create(
  persist(
    (set, get) => ({
      // ==================== State ====================
      // 用户信息
      user: null,
      role: null,
      isLoggedIn: false,
      studentId: null,  // 新增：学生ID
      
      // 用户权限列表
      permissions: [],
      
      // 加载状态
      loading: false,
      error: null,

      // ==================== Getters ====================
      
      /**
       * 检查是否有指定权限
       * @param {string} permissionId - 权限ID
       * @returns {boolean}
       */
      hasPermission: (permissionId) => {
        const state = get();
        
        // 未登录无权限
        if (!state.isLoggedIn) return false;
        
        // 管理员拥有所有权限
        if (state.role === UserRole.ADMIN) return true;
        
        return checkPermission(state.permissions, permissionId);
      },

      /**
       * 检查是否可访问模块
       * @param {string} module - 模块名
       * @returns {boolean}
       */
      canAccessModule: (module) => {
        const state = get();
        if (!state.isLoggedIn) return false;
        if (state.role === UserRole.ADMIN) return true;
        
        const routePermissionId = `${module}:route:access`;
        return checkPermission(state.permissions, routePermissionId);
      },

      /**
       * 检查是否为管理员
       */
      isAdmin: () => get().role === UserRole.ADMIN,
      
      /**
       * 检查是否为老师
       */
      isTeacher: () => get().role === UserRole.TEACHER,
      
      /**
       * 检查是否为学生/家长
       */
      isStudent: () => get().role === UserRole.STUDENT,

      /**
       * 获取学生ID
       */
      getStudentId: () => get().studentId,

      // ==================== Actions ====================

      /**
       * 本地登录 (Demo模式)
       * @param {Object} userData - 用户数据
       * @param {string} userData.username - 用户名
       * @param {string} userData.role - 角色
       * @param {string[]} [userData.permissions] - 自定义权限
       */
      login: (userData) => {
        const { username, role, permissions: customPermissions } = userData;
        
        // 获取角色默认权限
        const rolePermissions = getRolePermissions(role);
        
        // 合并自定义权限（如果有）
        const finalPermissions = customPermissions 
          ? [...new Set([...rolePermissions, ...customPermissions])]
          : rolePermissions;
        
        set({
          user: { username },
          role,
          permissions: finalPermissions,
          isLoggedIn: true,
          studentId: null, // 初始化学生ID
          error: null,
        });
        
        console.log('🔐 本地登录成功:', { username, role, permissionsCount: finalPermissions.length });
      },

      /**
       * API 登录 - 从后端响应设置用户状态
       * @param {Object} user - 用户信息
       * @param {string} access_token - 访问令牌
       */
      loginFromApi: async (user, access_token) => {
        // 从后端用户数据提取权限
        const permissions = user.permissions || getRolePermissions(user.role);
        
        // 设置基本用户信息
        set({
          user: {
            id: user.id,
            username: user.username,
            email: user.email,
            phone: user.phone,
            is_token_access: user.is_token_access || false,
          },
          role: user.role,
          permissions: Array.isArray(permissions) ? permissions : [permissions],
          isLoggedIn: true,
          studentId: user.student_id || null,  // 优先使用返回的 student_id
          error: null,
        });
        
        // 如果用户是学生角色且还没有 studentId，尝试获取（兼容旧登录）
        if (user.role === UserRole.STUDENT && !user.student_id) {
          try {
            const studentId = await authService.getCurrentStudentId();
            if (studentId) {
              set({ studentId });
              console.log('📚 学生ID已获取:', studentId);
            }
          } catch (error) {
            console.error('获取学生ID失败:', error);
          }
        }
        
        console.log('🔐 API登录成功:', { 
          username: user.username, 
          role: user.role, 
          studentId: user.student_id || get().studentId,
          permissionsCount: permissions.length 
        });
      },

      /**
       * setLoginInfo 别名，兼容部分组件调用
       */
      setLoginInfo: (user, token) => get().loginFromApi(user, token),

      /**
       * 登出
       */
      logout: () => {
        set({
          user: null,
          role: null,
          permissions: [],
          isLoggedIn: false,
          studentId: null,
          error: null,
        });
        console.log('🔓 已登出');
      },

      /**
       * 更新用户权限
       * @param {string[]} newPermissions - 新权限列表
       */
      updatePermissions: (newPermissions) => {
        set({ permissions: newPermissions });
        console.log('🔄 权限已更新:', newPermissions.length);
      },

      /**
       * 添加权限
       * @param {string} permissionId - 权限ID
       */
      addPermission: (permissionId) => {
        const state = get();
        if (!state.permissions.includes(permissionId)) {
          set({ permissions: [...state.permissions, permissionId] });
          console.log('➕ 添加权限:', permissionId);
        }
      },

      /**
       * 移除权限
       * @param {string} permissionId - 权限ID
       */
      removePermission: (permissionId) => {
        const state = get();
        set({ 
          permissions: state.permissions.filter(p => p !== permissionId) 
        });
        console.log('➖ 移除权限:', permissionId);
      },

      /**
       * 设置学生ID
       * @param {number} studentId - 学生ID
       */
      setStudentId: (studentId) => {
        set({ studentId });
        console.log('🎓 学生ID已设置:', studentId);
      },

      /**
       * 设置加载状态
       */
      setLoading: (loading) => set({ loading }),

      /**
       * 设置错误
       */
      setError: (error) => set({ error }),

      /**
       * 清除错误
       */
      clearError: () => set({ error: null }),
    }),
    {
      name: 'summer-art-permission-storage',
      partialize: (state) => ({
        user: state.user,
        role: state.role,
        permissions: state.permissions,
        isLoggedIn: state.isLoggedIn,
        studentId: state.studentId,  // 保存学生ID到持久化存储
      }),
    }
  )
);

export default usePermissionStore;
